import { useState } from "react";
import { Upload, Settings, Tag, RefreshCw, Code, MonitorSmartphone } from "lucide-react";

const steps = [
  {
    icon: Upload,
    title: "Install & Activate",
    content: `1. Download the plugin ZIP from this page.\n2. In WordPress, go to Plugins > Add New > Upload Plugin.\n3. Select the ZIP file and click "Install Now".\n4. Activate the plugin.`,
  },
  {
    icon: Settings,
    title: "Configure Settings",
    content: `1. Navigate to Amazon Products > Settings.\n2. Enter your Amazon affiliate tag (e.g. "yourtag-20").\n3. Select which Amazon domains to support.\n4. Configure sync batch size and timeout.\n5. Enable or disable price history tracking.\n6. Add your custom disclaimer text.`,
  },
  {
    icon: Tag,
    title: "Import Products",
    content: `1. Go to Amazon Products > Import.\n2. For single import: paste an Amazon product URL and click "Import".\n3. For bulk import: paste multiple URLs (one per line) and click "Bulk Import".\n4. The plugin extracts ASIN, fetches product data, and creates posts automatically.`,
  },
  {
    icon: RefreshCw,
    title: "Auto-Sync Setup",
    content: `By default, WP-Cron syncs products every 8 hours.\n\nFor more reliable scheduling, add a server cron:\n\n  */30 * * * * wget -q -O - https://yoursite.com/wp-cron.php?doing_wp_cron >/dev/null 2>&1\n\nOr use the REST API endpoint:\n  POST /wp-json/aap/v1/sync\n(Requires admin authentication.)`,
  },
  {
    icon: Code,
    title: "Display Products",
    content: `Use shortcodes in any post, page, or widget:\n\n  [affiliate_product id="142"]\n  [affiliate_grid category="electronics" columns="3" limit="6"]\n\nOr use the Gutenberg block:\n  1. Open the block editor.\n  2. Search for "Amazon Product".\n  3. Select a product from the dropdown.\n  4. The card preview renders live in the editor.`,
  },
  {
    icon: MonitorSmartphone,
    title: "Responsive Output",
    content: `Product cards are fully responsive.\n\nGrid layouts automatically adapt:\n- 4 columns on desktop\n- 2 columns on tablet\n- 1 column on mobile\n\nImages are lazy-loaded. The disclaimer appears automatically below every product card and grid.`,
  },
];

export default function Documentation() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section
      id="docs"
      data-testid="docs-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F9FAFB]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="inline-flex items-center gap-2 bg-[#FF7300]/10 text-[#FF7300] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Setup Guide
          </span>
          <h2 className="font-display font-extrabold text-[#09090B] text-3xl sm:text-4xl tracking-tight mb-4">
            Get Started in Minutes
          </h2>
          <p className="text-[#52525B] text-base md:text-lg max-w-xl mx-auto">
            From installation to displaying products — follow these simple steps.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar nav */}
          <div className="lg:col-span-4">
            <nav className="flex flex-col gap-1">
              {steps.map((step, i) => {
                const Icon = step.icon;
                const isActive = i === activeStep;
                return (
                  <button
                    key={i}
                    data-testid={`doc-step-${i}`}
                    onClick={() => setActiveStep(i)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all border-0 cursor-pointer w-full ${
                      isActive
                        ? "bg-white border border-[#E4E4E7] shadow-sm"
                        : "bg-transparent hover:bg-white/60"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                        isActive ? "bg-[#FF7300] text-white" : "bg-[#E4E4E7] text-[#52525B]"
                      }`}
                    >
                      <Icon size={16} />
                    </div>
                    <div>
                      <span className="text-xs text-[#A1A1AA] font-mono">Step {i + 1}</span>
                      <div className={`text-sm font-semibold ${isActive ? "text-[#09090B]" : "text-[#52525B]"}`}>
                        {step.title}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content area */}
          <div className="lg:col-span-8">
            <div className="bg-white border border-[#E4E4E7] rounded-lg p-8 min-h-[300px] animate-fade-in" key={activeStep}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-md bg-[#FF7300] flex items-center justify-center">
                  {(() => {
                    const Icon = steps[activeStep].icon;
                    return <Icon size={20} className="text-white" />;
                  })()}
                </div>
                <div>
                  <span className="text-xs text-[#A1A1AA] font-mono">Step {activeStep + 1} of {steps.length}</span>
                  <h3 className="font-display font-bold text-[#09090B] text-lg">{steps[activeStep].title}</h3>
                </div>
              </div>

              <div className="font-mono text-sm leading-loose text-[#09090B] whitespace-pre-wrap">
                {steps[activeStep].content}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
